import React, {
  createContext, useState, useEffect, useContext, ReactNode
} from 'react';
import { BACKEND } from '../config';
import LoadingSpinner from '../components/Spinner/LoadingSpinner';
import { SESSION_EXPIRED_EVENT, resetSessionExpired } from '../utils/sessionExpiry';

interface AuthContextType {
  isAuthenticated: boolean;
  role: 'admin' | 'manager' | 'employee' | 'notary' | 'secondary' | 'lawyer' | 'mediator' | 'representative_employee' | 'chairman' | 'representative' | null;
  userId: number | null;
  username: string | null;
  isLawyer: boolean;
  isMediator: boolean;
  refreshSession: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  role: null,
  userId: null,
  username: null,
  isLawyer: false,
  isMediator: false,
  refreshSession: async () => {},
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState<'admin' | 'manager' | 'employee' | 'notary' | 'secondary' | 'lawyer' | 'mediator' | 'representative_employee' | 'chairman' | 'representative' | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isLawyer, setIsLawyer] = useState(false);
  const [isMediator, setIsMediator] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshSession = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/check-session/`, {
        credentials: 'include',
      });
      const data = await res.json();
      setIsAuthenticated(data.authenticated);
      setRole(data.role as any);
      setUserId(data.user_id || null); // Assuming the API returns `user_id`
      setUsername(data.username || null);
      setIsLawyer(Boolean(data.is_lawyer));
      setIsMediator(Boolean(data.is_mediator));
      if (data.authenticated) resetSessionExpired();
    } catch {
      setIsAuthenticated(false);
      setRole(null);
      setUserId(null);
      setUsername(null);
      setIsLawyer(false);
      setIsMediator(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    refreshSession();
  }, []);

  // Истёкшая сессия (репорт из csrfFetch/apiClient на 401): сбрасываем auth-состояние —
  // все поллеры/SSE останавливаются своими guard'ами по isAuthenticated, роуты уводят на /login.
  useEffect(() => {
    const onExpired = () => {
      setIsAuthenticated(false);
      setRole(null);
      setUserId(null);
      setUsername(null);
      setIsLawyer(false);
      setIsMediator(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <AuthContext.Provider value={{ isAuthenticated, role, userId, username, isLawyer, isMediator, refreshSession, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
